import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { useVoicemailsStore } from '../store/voicemailsStore.js'
import type { Voicemail } from '../store/voicemailsStore.js'

export type { Voicemail }

export interface UseMyVoicemailsOptions {
  /** Filter by voicemail status. When `null`, omit the status param (fetches all non-deleted). */
  status: 'NEW' | 'HEARD' | 'DELETED' | null
}

/**
 * Fetches the current agent's voicemails from
 * GET /voicemails?status=<status>&limit=50
 * and writes them into the singleton `voicemailsStore`.
 *
 * The backend auto-scopes to `agentId = self` for non-admin callers
 * (VOICEMAIL_VIEW_OWN permission) — this hook does NOT pass agentId.
 *
 * UI-5b Task 8: the list lives in `voicemailsStore`. This hook fetches +
 * writes via `setMyVoicemails` and returns the list by subscribing to the
 * store, so socket-driven patches reach consumers automatically. `loading`
 * stays local. No polling — the VoicemailBadge (Task 12) handles its own
 * polling; consumers call `refetch` on demand.
 */
export function useMyVoicemails(options: UseMyVoicemailsOptions): {
  voicemails: Voicemail[]
  loading: boolean
  fetchError: boolean
  refetch: () => Promise<void>
} {
  const { status } = options
  const voicemails = useVoicemailsStore((s) => s.myVoicemails)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  // Mounted guard: prevents setState calls after unmount.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const query = status
        ? `/voicemails?status=${status}&limit=50`
        : `/voicemails?limit=50`
      const resp = await api.get<{ voicemails: Voicemail[] }>(query)
      if (mountedRef.current) {
        useVoicemailsStore.getState().setMyVoicemails(resp.voicemails)
        setFetchError(false)
      }
    } catch {
      if (mountedRef.current) {
        setFetchError(true)
      }
      if (import.meta.env.DEV) {
        console.warn('[useMyVoicemails] fetch failed')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [status])

  // Fetch on mount and whenever status changes.
  useEffect(() => {
    void load()
  }, [load])

  return { voicemails, loading, fetchError, refetch: load }
}
